//@ts-nocheck
// GCC COMPATIBILITY RESULT - Displays compatibility reading after streaming completes
import React, { useState, useEffect, useRef } from "react";
import {
  ChevronLeft,
  Heart,
  Clock,
  MessageCircle,
  Star,
  Sun,
  Zap,
} from "lucide-react";
import { useChat } from "../../contexts/ChatContext";
import { JapanCompatibilityLoadingView } from "./JapanCompatibilityResult";

interface GCC3BoxData {
  energy_signature: "Soft" | "Balanced" | "Deep" | null;
  dob: string | null;
  destiny_time: string | null;
  birth_time: string | null;
  birth_city: string | null;
}

interface GCCCompatibilityResponse {
  pages?: {
    pageId: string;
    title: string;
    components?: {
      scoreGauge?: { value: number; label: string };
      lifeGraph?: { categories: string[]; value: number[] };
      summary?: { type: string; title: string; text: string }[];
    };
    cards?: { id: string; title: string; icon: string; description: string }[];
  }[];
}

const CARD_ICONS: Record<string, React.ReactNode> = {
  heart: <Heart size={20} />,
  clock: <Clock size={20} />,
  wave: <MessageCircle size={20} />,
  star: <Star size={20} />,
  sun: <Sun size={20} />,
  message: <MessageCircle size={20} />,
  growth: <Zap size={20} />,
};

interface GCCCompatibilityResultProps {
  selfData: GCC3BoxData;
  partnerData: GCC3BoxData;
  onBack?: () => void;
}

const GCCCompatibilityResult: React.FC<GCCCompatibilityResultProps> = ({
  selfData,
  partnerData,
  onBack,
}) => {
  const { chats, currentChat, sendMessageStream } = useChat();
  const [result, setResult] = useState<GCCCompatibilityResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  // Refs to avoid stale closures
  const chatsRef = useRef(chats);
  const currentChatRef = useRef(currentChat);
  chatsRef.current = chats;
  currentChatRef.current = currentChat;

  useEffect(() => {
    let isMounted = true;

    const fetchResult = async () => {
      try {
        // Use the EXISTING chat - find it from chats array
        const existingChat = chatsRef.current.find(
          (c) =>
            c.categoryId &&
            c.subCategoryId &&
            c.subCategoryId.toLowerCase().includes("compatibility"),
        );

        // Fallback to currentChat
        const targetChat = existingChat || currentChatRef.current;

        if (!targetChat) {
          console.error("GCC Result - No existing GCC chat found");
          if (isMounted) {
            setError(
              "No chat session found. Please start from the GCC Compatibility option.",
            );
            setIsLoading(false);
          }
          return;
        }

        // console.log(
        //   "GCC Result - Using chat:",
        //   targetChat.id,
        //   targetChat.categoryId,
        //   targetChat.subCategoryId,
        // );

        const targetChatId = targetChat.id;
        const effectiveCategoryId = targetChat.categoryId;
        const effectiveSubCategoryId = targetChat.subCategoryId;

        // Build message
        const selfSummary = [
          selfData.energy_signature && `Energy: ${selfData.energy_signature}`,
          selfData.dob && `DOB: ${selfData.dob}`,
          selfData.destiny_time && `Time: ${selfData.destiny_time}`,
        ]
          .filter(Boolean)
          .join(", ");

        const partnerSummary = [
          partnerData.energy_signature &&
            `Energy: ${partnerData.energy_signature}`,
          partnerData.dob && `DOB: ${partnerData.dob}`,
          partnerData.destiny_time && `Time: ${partnerData.destiny_time}`,
        ]
          .filter(Boolean)
          .join(", ");

        const autoMessage = `Check compatibility\nPerson A (Me): ${selfSummary}\nPerson B (Partner): ${partnerSummary}`;

        // Call sendMessageStream using the EXISTING chat
        await sendMessageStream(
          autoMessage,
          targetChatId,
          effectiveCategoryId,
          effectiveSubCategoryId,
          null, // spanishTone
          null, // japan3BoxSelf
          null, // japan3BoxPartner
          null, // korea3BoxSelf
          null, // korea3BoxPartner
          selfData, // gcc3BoxSelf
          partnerData, // gcc3BoxPartner
        );

        // console.log("GCC Result - sendMessageStream completed");
        // console.log("GCC Result - currentChatRef:", currentChatRef.current);
        // console.log(
        //   "GCC Result - chatsRef:",
        //   chatsRef.current.map((c) => ({
        //     id: c.id,
        //     gcc: c.gccCompatibilityData,
        //   })),
        // );

        // After await, check currentChat for gccCompatibilityData
        const updatedChat = currentChatRef.current;

        if (updatedChat?.gccCompatibilityData) {
          if (isMounted) {
            setResult(
              updatedChat.gccCompatibilityData as GCCCompatibilityResponse,
            );
            setIsLoading(false);
          }
          return;
        }

        // Fallback: check the chat in chats array
        const chatFromArray = chatsRef.current.find(
          (c) => c.id === targetChatId,
        );
        const lastAiMsg = chatFromArray?.messages
          ?.filter((m: any) => m.sender === "ai")
          .slice(-1)[0];

        if (lastAiMsg?.content) {
          try {
            const jsonMatch = lastAiMsg.content.match(/\{[\s\S]*\}/);
            if (jsonMatch) {
              const parsed = JSON.parse(jsonMatch[0]);
              if (parsed.pages) {
                if (isMounted) {
                  setResult(parsed as GCCCompatibilityResponse);
                  setIsLoading(false);
                }
                return;
              }
            }
          } catch (e) {
            // Not JSON
          }
        }

        if (isMounted) {
          setResult({
            pages: [
              {
                pageId: "P2_DetailedInsights",
                title: "Compatibility Insights",
                cards: [
                  {
                    id: "summary",
                    title: "Compatibility Reading",
                    icon: "heart",
                    description:
                      lastAiMsg?.content ||
                      "Thank you for checking your compatibility.",
                  },
                ],
              },
            ],
          });
          setIsLoading(false);
        }
      } catch (err) {
        console.error("GCC Result Error:", err);
        if (isMounted) {
          setError("Failed to get compatibility reading. Please try again.");
          setIsLoading(false);
        }
      }
    };

    fetchResult();

    return () => {
      isMounted = false;
    };
  }, []);

  // ─── Styles ───────────────────────────────────────────────────────────────
  const C = {
    purple: "#6D4C92",
    purpleSoft: "rgba(109,76,146,0.12)",
    purpleBorder: "rgba(109,76,146,0.4)",
    white: "#fff",
    lightBg: "#F8F4FF",
    lightCard: "#fff",
    lightBorder: "#E8E4DE",
    textDark: "#3D3A35",
    textMuted: "#9A9590",
  };

  const styles: Record<string, any> = {
    container: {
      flex: 1,
      overflowY: "auto" as const,
      background: C.lightBg,
      minHeight: "100%",
      display: "flex" as const,
      flexDirection: "column" as const,
    },
    content: {
      padding: "20px 16px 40px",
      maxWidth: 480,
      margin: "0 auto",
      width: "100%" as const,
      display: "flex" as const,
      flexDirection: "column" as const,
      alignItems: "center" as const,
    },
    loading: {
      textAlign: "center" as const,
      padding: "60px 20px",
    },
    loadingDots: {
      display: "flex",
      justifyContent: "center",
      gap: 8,
      marginBottom: 20,
    },
    loadingDot: {
      width: 12,
      height: 12,
      borderRadius: "50%",
      background: C.purple,
      animation: "pulse 1s infinite",
    },
    resultCard: {
      borderRadius: 16,
      background: C.lightCard,
      border: `1px solid ${C.lightBorder}`,
      padding: 20,
      marginBottom: 16,
      width: "100%",
    },
    scoreContainer: {
      textAlign: "center" as const,
      marginBottom: 20,
    },
    scoreValue: {
      fontSize: 48,
      fontWeight: 700,
      color: C.purple,
    },
    scoreLabel: {
      fontSize: 16,
      color: C.textMuted,
      marginTop: 4,
    },
    insightCard: {
      background: C.purpleSoft,
      borderRadius: 12,
      padding: 16,
      marginBottom: 12,
    },
    insightTitle: {
      fontSize: 15,
      fontWeight: 600,
      color: C.purple,
      marginBottom: 8,
      display: "flex",
      alignItems: "center",
      gap: 8,
    },
    insightText: {
      fontSize: 14,
      color: C.textDark,
      lineHeight: 1.7,
    },
    error: {
      padding: "12px 16px",
      background: "#FEE2E2",
      border: "1px solid #EF4444",
      borderRadius: 8,
      color: "#DC2626",
      fontSize: 14,
      marginBottom: 16,
    },
    btn: {
      width: "100%",
      height: 52,
      borderRadius: 14,
      background: C.purple,
      color: C.white,
      fontSize: 15,
      fontWeight: 600,
      border: "none",
      cursor: "pointer",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      gap: 8,
    },
  };

  return (
    <div style={styles.container}>
      <style>{`@keyframes pulse { 0%, 100% { opacity: 0.4; transform: scale(0.9); } 50% { opacity: 1; transform: scale(1); } }`}</style>

      <div style={styles.content}>
        {onBack && (
          <button
            onClick={onBack}
            style={{
              alignSelf: "flex-end",
              background: "none",
              border: "none",
              color: C.textMuted,
              cursor: "pointer",
              display: "flex",
              alignItems: "center",
              gap: 4,
              marginBottom: 16,
              fontSize: 14,
            }}
          >
            <ChevronLeft size={16} /> Back to Form
          </button>
        )}

        {/* Loading */}
        {/* {isLoading && (
          <div style={styles.loading}>
            <div style={styles.loadingDots}>
              {[0, 1, 2].map((i) => (
                <div
                  key={i}
                  style={{
                    ...styles.loadingDot,
                    animationDelay: `${i * 0.2}s`,
                  }}
                />
              ))}
            </div>
            <p style={{ fontSize: 14, color: C.textMuted }}>
              Analyzing your compatibility with calm clarity...
            </p>
          </div>
        )} */}
        {isLoading && (
          <JapanCompatibilityLoadingView
            selfBloodType={selfData?.energy_signature}
            partnerBloodType={partnerData?.energy_signature}
          />
        )}

        {/* Error */}
        {!isLoading && error && <div style={styles.error}>{error}</div>}

        {/* Result */}
        {!isLoading && result && (
          <>
            {/* Score */}
            {result.pages?.[0]?.components?.scoreGauge && (
              <div style={styles.resultCard}>
                <div style={styles.scoreContainer}>
                  <div style={styles.scoreValue}>
                    {result.pages[0].components.scoreGauge.value}
                  </div>
                  <div style={styles.scoreLabel}>
                    {result.pages[0].components.scoreGauge.label}
                  </div>
                </div>

                {/* Summary */}
                {result.pages[0].components.summary?.map(
                  (item: any, idx: number) => (
                    <div
                      key={idx}
                      style={{
                        ...styles.insightCard,
                        background: idx === 0 ? C.purpleSoft : "#FEF9E7",
                      }}
                    >
                      <div style={styles.insightTitle}>
                        <span>{idx === 0 ? "✨" : "💡"}</span>
                        {item.title}
                      </div>
                      <p style={styles.insightText}>{item.text}</p>
                    </div>
                  ),
                )}
              </div>
            )}

            {/* Insight Cards */}
            {result.pages
              ?.find((p: any) => p.pageId === "P2_DetailedInsights")
              ?.cards?.map((card: any, idx: number) => (
                <div key={idx} style={styles.resultCard}>
                  <div style={styles.insightTitle}>
                    {CARD_ICONS[card.icon] || <Heart size={20} />}
                    {card.title}
                  </div>
                  <p style={styles.insightText}>{card.description}</p>
                </div>
              ))}

            {/* Fallback if no structured data */}
            {!result.pages?.find(
              (p: any) => p.pageId === "P2_DetailedInsights",
            ) && (
              <div style={styles.resultCard}>
                <div style={styles.insightTitle}>
                  <Heart size={20} />
                  Compatibility Reading
                </div>
                <p style={styles.insightText}>
                  Your compatibility has been analyzed. Please check the API
                  response for detailed insights.
                </p>
              </div>
            )}

            {onBack && (
              <button style={styles.btn} onClick={onBack}>
                Check Another Compatibility
              </button>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default GCCCompatibilityResult;
